import { spawn, type ChildProcess } from "node:child_process";
import { createServer } from "node:http";
import { existsSync, readFileSync, watch, type FSWatcher } from "node:fs";
import { dirname, join, resolve } from "node:path";
import { discoverAgents, loadAgent, loadAgentById } from "../loader";
import { discoverAgent } from "../discover/index";
import { streamAgent } from "../runner/index";
import { showHeader } from "./banner";
import { grey, dimmed } from "./style";
import { startBlockChat } from "./tui/renderer/start-block-chat";
import { handleSessionsRequest, getProviderApiKey } from "../server/index";

export interface DevOptions {
  port: string;
  agentDir: string;
  input?: boolean;
  /** Skip auto-starting the channels/web/ dev server even when it exists. */
  noWeb?: boolean;
  /** Skip auto-opening the browser at the web channel URL. */
  noOpen?: boolean;
}

function checkProviderKeys(modelId: string): string[] {
  const provider = modelId.split("/")[0];
  const missing: string[] = [];

  const keyMap: Record<string, string> = {
    openai: "OPENAI_API_KEY",
    anthropic: "ANTHROPIC_API_KEY",
    groq: "GROQ_API_KEY",
    deepseek: "DEEPSEEK_API_KEY",
    mistral: "MISTRAL_API_KEY",
    google: "GOOGLE_API_KEY",
    meta: "TOGETHER_API_KEY",
  };

  const envVar = keyMap[provider];
  if (envVar && !process.env[envVar] && !getProviderApiKey(provider)) {
    missing.push(envVar);
  }

  return missing;
}

const MAX_PORT_ATTEMPTS = 10;

function tryListen(server: ReturnType<typeof createServer>, port: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const onError = (err: NodeJS.ErrnoException) => {
      server.removeListener("listening", onListening);
      reject(err);
    };
    const onListening = () => {
      server.removeListener("error", onError);
      resolve();
    };
    server.once("error", onError);
    server.once("listening", onListening);
    server.listen(port);
  });
}

async function listenWithFallback(
  server: ReturnType<typeof createServer>,
  startPort: number,
): Promise<number> {
  for (let offset = 0; offset < MAX_PORT_ATTEMPTS; offset += 1) {
    const port = startPort + offset;
    try {
      await tryListen(server, port);
      return port;
    } catch (err) {
      const code = (err as NodeJS.ErrnoException).code;
      if (code !== "EADDRINUSE") throw err;
    }
  }
  throw new Error(
    `Could not find a free port in ${startPort}..${startPort + MAX_PORT_ATTEMPTS - 1}`,
  );
}

function isPortFree(port: number): Promise<boolean> {
  return new Promise((resolve) => {
    const probe = createServer();
    probe.once("error", () => resolve(false));
    probe.once("listening", () => {
      probe.close(() => resolve(true));
    });
    probe.listen(port);
  });
}

async function findFreePort(startPort: number, maxAttempts = MAX_PORT_ATTEMPTS): Promise<number> {
  for (let offset = 0; offset < maxAttempts; offset += 1) {
    const port = startPort + offset;
    if (await isPortFree(port)) return port;
  }
  throw new Error(`No free port in ${startPort}..${startPort + maxAttempts - 1}`);
}

function openBrowser(url: string): void {
  const command =
    process.platform === "darwin" ? "open"
      : process.platform === "win32" ? "start"
      : "xdg-open";
  try {
    const child = spawn(command, [url], {
      detached: true,
      stdio: "ignore",
      shell: process.platform === "win32",
    });
    child.unref();
  } catch {
    // Non-fatal: user can copy the URL from the console.
  }
}

async function waitForHttp(url: string, timeoutMs = 30_000): Promise<boolean> {
  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    try {
      const res = await fetch(url, { method: "GET" });
      if (res.status < 500) return true;
    } catch {
      // fetch throws for connection refused — keep polling
    }
    await new Promise((r) => setTimeout(r, 500));
  }
  return false;
}

interface WebChannelHandle {
  readonly url: string;
  readonly process: ChildProcess;
}

/**
 * Spawns the channels/web/ dev server as a child process, waits for it to
 * come up, and returns a handle the caller can kill on shutdown. Returns
 * undefined when the channel isn't scaffolded, isn't installed, or fails
 * to start in time.
 */
async function installWebDeps(webDir: string): Promise<boolean> {
  return new Promise((resolve) => {
    const child = spawn("npm", ["install", "--no-fund", "--no-audit"], {
      cwd: webDir,
      stdio: "ignore",
    });
    child.once("exit", (code) => resolve(code === 0));
    child.once("error", () => resolve(false));
  });
}

async function startWebChannel(
  agentDir: string,
  webPort: number,
): Promise<WebChannelHandle | undefined> {
  const projectRoot = dirname(agentDir);
  const webDir = join(projectRoot, "channels", "web");
  if (!existsSync(join(webDir, "package.json"))) return undefined;

  if (!existsSync(join(webDir, "node_modules"))) {
    console.log(`  ${dimmed(`web    installing channels/web deps (first run)…`)}`);
    const installed = await installWebDeps(webDir);
    if (!installed) {
      console.log();
      console.log(`  ${grey("⚠")} channels/web install failed — try manually:`);
      console.log(`  ${dimmed(`  cd ${webDir} && npm install`)}`);
      console.log();
      return undefined;
    }
  }

  // ARCIE_AGENT_DIR points the Next.js /api/chat route at the agent
  // files it should run. streamAgent() is called in-process now — no
  // proxying, no separate arcie HTTP server. PORT lets Next.js pick the
  // port arcie chose (avoids the parsing quirk with `-- --port`).
  const child = spawn("npm", ["run", "dev"], {
    cwd: webDir,
    env: {
      ...process.env,
      ARCIE_AGENT_DIR: agentDir,
      PORT: String(webPort),
    },
    stdio: "ignore",
    detached: false,
  });

  const url = `http://localhost:${webPort}`;
  const ready = await waitForHttp(url, 45_000);
  if (!ready) {
    console.log();
    console.log(`  ${grey("⚠")} channels/web didn't come up within 45s`);
    child.kill();
    return undefined;
  }

  return { url, process: child };
}

export async function devCommand(options: DevOptions): Promise<void> {
  const agentDirPath = resolve(process.cwd(), options.agentDir);
  const requestedPort = parseInt(options.port, 10);

  // Load .env.local from the project root before anything reads env keys.
  // The user puts CENCORI_API_KEY here; if we don't load it, both this
  // process and the spawned `next dev` see nothing.
  loadDotEnv(join(dirname(agentDirPath), ".env.local"));

  if (!process.env.CENCORI_API_KEY) process.env.CENCORI_API_KEY = "local-dev-key";

  showHeader();

  const { diagnostics } = discoverAgent(agentDirPath);
  if (diagnostics.some((d) => d.severity === "error")) {
    for (const d of diagnostics) console.error(`  ${grey("✖")} ${d.code}: ${d.message}`);
    process.exit(1);
  }
  for (const d of diagnostics) console.warn(`  ${grey("⚠")} ${d.code}: ${d.message}`);

  let modelLine = agentDirPath;
  let missingKeys: string[] = [];
  try {
    const agent = await loadAgent(agentDirPath);
    modelLine = `${agentDirPath} ${grey("\xB7")} ${grey(agent.manifest.config.model)}`;
    missingKeys = checkProviderKeys(agent.manifest.config.model);
  } catch {
    /* fall through — dev still runs, /api/chat will error clearly */
  }
  console.log(`  ${modelLine}`);
  console.log();

  const projectRoot = dirname(agentDirPath);
  const webDir = join(projectRoot, "channels", "web");
  const hasWeb = existsSync(webDir);
  const wantsWeb = !options.input && options.noWeb !== true && hasWeb;

  // ── Web-attached mode: one process, one port. Next.js owns /api/chat
  //    and calls streamAgent() in-process. No proxy, no separate arcie HTTP.
  if (wantsWeb) {
    let webPort: number;
    try {
      webPort = await findFreePort(requestedPort);
    } catch (err) {
      console.error(`  ${grey("✗")} ${err instanceof Error ? err.message : String(err)}`);
      process.exit(1);
    }
    if (webPort !== requestedPort) {
      console.log(`  ${grey("!")} port ${requestedPort} was in use ${grey("\xB7")} using ${webPort}`);
      console.log();
    }
    console.log(`  ${dimmed(`starting on http://localhost:${webPort}…`)}`);
    const webChannel = await startWebChannel(agentDirPath, webPort);
    if (webChannel === undefined) {
      console.log(`  ${grey("⚠")} web channel failed to start`);
      process.exit(1);
    }
    console.log(`  ${dimmed(`web    http://localhost:${webPort}`)}`);
    console.log(`  ${dimmed(`api    http://localhost:${webPort}/api/chat`)}`);
    if (missingKeys.length > 0) {
      console.log();
      console.log(`  ${grey("⚠")} Missing API keys: ${missingKeys.join(", ")}`);
      console.log(`  ${dimmed("  Set them in .env.local or your environment")}`);
    }
    console.log();
    console.log(`  ${dimmed("hot reload  edits to agent/*.ts land on the next request")}`);
    console.log();
    console.log(`  ${dimmed("Ctrl+C to stop")}`);
    console.log();

    if (options.noOpen !== true) openBrowser(webChannel.url);

    const watcher = startAgentWatcher(agentDirPath);
    const shutdown = () => {
      watcher?.close();
      webChannel.process.kill();
      process.exit(0);
    };
    process.once("SIGINT", shutdown);
    process.once("SIGTERM", shutdown);
    return;
  }

  // ── JSON-only mode: no channels/web/. Standalone HTTP server for
  //    curl users, tests, and other connectors that hit the agent
  //    directly (Slack, WhatsApp bots, etc. down the line).
  const streamTurn = async (
    res: import("node:http").ServerResponse,
    body: string,
    agentId: string | undefined,
  ) => {
    try {
      const { message, stream } = JSON.parse(body);
      if (typeof message !== "string" || message.length === 0) {
        res.writeHead(400, { "Content-Type": "application/json" });
        res.end(JSON.stringify({ error: "message is required" }));
        return;
      }
      const runOpts = {
        hotReload: true,
        ...(agentId !== undefined ? { agentId } : {}),
      };
      if (stream) {
        res.writeHead(200, {
          "Content-Type": "application/x-ndjson",
          "Cache-Control": "no-cache",
          Connection: "keep-alive",
        });
        for await (const event of streamAgent(agentDirPath, message, runOpts)) {
          res.write(JSON.stringify(event) + "\n");
        }
        res.end();
      } else {
        res.writeHead(200, { "Content-Type": "application/json" });
        for await (const _event of streamAgent(agentDirPath, message, runOpts)) {}
        res.end(JSON.stringify({ status: "ok" }));
      }
    } catch (err) {
      res.writeHead(500, { "Content-Type": "application/json" });
      res.end(JSON.stringify({ error: String(err) }));
    }
  };

  const listAgents = async () => {
    const discovered = discoverAgents(agentDirPath);
    return Promise.all(
      discovered.map(async ({ id }) => {
        try {
          const loaded = await loadAgentById(agentDirPath, id, { hotReload: true });
          const { config } = loaded.manifest;
          return {
            id,
            name: config.name ?? id,
            model: config.model,
            description: config.description ?? "",
          };
        } catch {
          return { id, name: id, model: "", description: "" };
        }
      }),
    );
  };

  const server = createServer(async (req, res) => {
    if (await handleSessionsRequest(req, res)) return;

    const method = req.method ?? "GET";
    const url = req.url ?? "/";

    if (method === "GET" && url === "/agents") {
      res.writeHead(200, { "Content-Type": "application/json" });
      res.end(JSON.stringify(await listAgents()));
      return;
    }

    const agentsMatch = url.match(/^\/agents\/([^/?]+)(?:\?.*)?$/);
    if (method === "POST" && agentsMatch !== null) {
      let body = "";
      for await (const chunk of req) body += chunk;
      await streamTurn(res, body, decodeURIComponent(agentsMatch[1]!));
      return;
    }

    if (method === "POST" && url === "/") {
      let body = "";
      for await (const chunk of req) body += chunk;
      await streamTurn(res, body, undefined);
      return;
    }

    res.writeHead(404);
    res.end("Not found");
  });

  let boundPort: number;
  try {
    boundPort = await listenWithFallback(server, requestedPort);
  } catch (err) {
    console.error();
    console.error(`  ${grey("✗")} ${err instanceof Error ? err.message : String(err)}`);
    console.error(`  ${dimmed("try: arcie dev --port <n>    # pick your own starting port")}`);
    console.error();
    process.exit(1);
  }

  if (!process.env.CENCORI_API_URL) {
    process.env.CENCORI_API_URL = `http://127.0.0.1:${boundPort}/v1`;
  }

  if (boundPort !== requestedPort) {
    console.log(`  ${grey("!")} port ${requestedPort} was in use ${grey("\xB7")} using ${boundPort}`);
    console.log();
  }
  console.log(`  ${dimmed(`agent  http://localhost:${boundPort}`)}`);
  if (missingKeys.length > 0) {
    console.log();
    console.log(`  ${grey("⚠")} Missing API keys: ${missingKeys.join(", ")}`);
    console.log(`  ${dimmed("  Set them in .env.local or your environment")}`);
  }
  console.log();
  console.log(`  ${dimmed("hot reload  edits to agent/*.ts land on the next request")}`);
  console.log();
  console.log(`  ${dimmed("Ctrl+C to stop")}`);
  console.log();

  const watcher = startAgentWatcher(agentDirPath);
  const shutdown = () => {
    watcher?.close();
    server.close();
    process.exit(0);
  };
  process.once("SIGINT", shutdown);
  process.once("SIGTERM", shutdown);

  if (options.input) {
    void startBlockChat({ agentDir: agentDirPath });
  }
}

/**
 * Watches the agent directory for `.ts` / `.md` changes and logs which files
 * changed. Actual hot-reload happens in the loader (cache-busted import per
 * request) — the watcher is purely informational.
 */
function startAgentWatcher(agentDirPath: string): FSWatcher | undefined {
  try {
    let debounce: ReturnType<typeof setTimeout> | undefined;
    const seen = new Set<string>();
    return watch(agentDirPath, { recursive: true }, (_event, filename) => {
      if (typeof filename !== "string") return;
      if (!filename.endsWith(".ts") && !filename.endsWith(".md")) return;
      seen.add(filename);
      if (debounce !== undefined) clearTimeout(debounce);
      debounce = setTimeout(() => {
        const files = [...seen].sort();
        seen.clear();
        for (const file of files) {
          console.log(`  ${dimmed(`reload · ${file}`)}`);
        }
      }, 150);
    });
  } catch {
    return undefined;
  }
}

function loadDotEnv(path: string): void {
  if (!existsSync(path)) return;
  try {
    const content = readFileSync(path, "utf-8");
    for (const line of content.split("\n")) {
      const trimmed = line.trim();
      if (trimmed.startsWith("#") || !trimmed.includes("=")) continue;
      const eq = trimmed.indexOf("=");
      const key = trimmed.slice(0, eq).trim();
      let value = trimmed.slice(eq + 1).trim();
      if (
        (value.startsWith('"') && value.endsWith('"')) ||
        (value.startsWith("'") && value.endsWith("'"))
      ) {
        value = value.slice(1, -1);
      }
      if (key && value && !process.env[key]) process.env[key] = value;
    }
  } catch {
    /* ignore */
  }
}
